import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
  useMemo,
} from 'react'
import dayjs from 'dayjs'
import req from '@sportninja/common/api/request'
import { myScheduleReducer, initialState } from './reducer'
import { resolveEventDateWindow } from './resolveEventDateWindow'
import {
  MyScheduleContextValue,
  ActionType,
  Organization,
  Schedule,
  Team,
  Venue,
  Facility,
  TimePeriod,
  MembershipType,
  SortOrder,
} from './types'
import { Dayjs } from 'dayjs'

const MyScheduleContext = createContext<MyScheduleContextValue | undefined>(
  undefined
)

export const MyScheduleContextProvider: React.FC<{
  children: React.ReactNode
}> = ({ children }) => {
  const [state, dispatch] = useReducer(myScheduleReducer, initialState)

  /**
   * Mark filters as loaded on mount
   */
  useEffect(() => {
    dispatch({ type: ActionType.SET_FILTERS_LOADED, payload: true })
  }, [])

  /**
   * Construct filter parameters for API calls
   */
  const getFilterParams = useCallback(() => {
    return {
      schedule_id: state.selectedSchedule?.id,
      team_id: state.selectedTeam?.id,
      organization_id: state.selectedOrganization?.id,
      venue_id: state.selectedVenue?.id,
      facility_id: state.selectedFacility?.id,
      // recursive: state.selectedOrganization?.id ? 1 : 1,
      recursive: 1,
      ...(!state.selectedOrganization?.id && {
        membership_type: state.selectedMembershipType,
      }),
      per_page: state.calendarMode ? 200 : 45,
    }
  }, [
    state.selectedSchedule?.id,
    state.selectedTeam?.id,
    state.selectedOrganization,
    state.selectedVenue?.id,
    state.selectedFacility?.id,
    state.selectedMembershipType,
    state.calendarMode,
  ])

  /**
   * Get date range based on selected time period or custom date range
   */
  /**
   * Query params for the games request.
   *
   * The window itself comes from `resolveEventDateWindow`, shared with the
   * event filtering in `MyScheduleList` so games and events can never be
   * bounded differently. Only the transport shape is built here: `order`, and
   * dropping a null `starts_after` (the endpoint expects the key absent
   * rather than null).
   */
  const getDateRange = useCallback(() => {
    const { startsAfter, startsBefore } = resolveEventDateWindow({
      dateRange: state.selectedDateRange,
      timePeriod: state.selectedTimePeriod,
      scheduleStartsAt: state.selectedSchedule?.starts_at,
    })

    return {
      ...(startsAfter !== null && { starts_after: startsAfter }),
      ...(startsBefore !== null && { starts_before: startsBefore }),
      order: state.selectedSortOrder,
    }
  }, [
    state.selectedDateRange,
    state.selectedTimePeriod,
    state.selectedSortOrder,
    state.selectedSchedule?.starts_at,
  ])

  /**
   * The exact query the games request sends, filters AND date window.
   *
   * Exposed on the context so the EVENTS request can send the same object:
   * `/events/membership` takes the same filters as `/games/follow`, and
   * building the query in one place is what guarantees the two lists can never
   * be bounded differently. `page` is not included — it is games-only
   * pagination, and the events endpoint returns its list unpaginated.
   */
  const scheduleQuery = useMemo(
    () => ({ ...getFilterParams(), ...getDateRange() }),
    [getFilterParams, getDateRange]
  )

  /**
   * The entity filters ALONE — no date window.
   *
   * The opening-period decision needs these with its OWN ±6 month window, not
   * the selected period's. Spreading `scheduleQuery` there instead put the
   * period in the decision query's key, so it refetched on every tab switch —
   * two `/events/membership` calls per switch instead of one.
   */
  const entityFilters = useMemo(() => getFilterParams(), [getFilterParams])

  /**
   * Fetch games based on current filters
   */
  const fetchGames = useCallback(async () => {
    try {
      dispatch({ type: ActionType.SET_LOADING_GAMES, payload: true })

      const filterParams = getFilterParams()
      const dateRange = getDateRange()

      const query = {
        ...filterParams,
        ...dateRange,
        page: 1,
      } as any

      const { data, meta } = await req('/games/follow', { query })

      dispatch({
        type: ActionType.SET_GAMES,
        payload: {
          games: data || [],
          pagination: meta?.pagination || null,
        },
      })
    } catch (error) {
      console.error('🚨 Error fetching games:', error)
      dispatch({
        type: ActionType.SET_GAMES,
        payload: { games: [], pagination: null },
      })
    } finally {
      dispatch({ type: ActionType.SET_LOADING_GAMES, payload: false })
    }
  }, [getFilterParams, getDateRange])

  /**
   * Fetch games for a specific page (pagination)
   */
  const fetchGamesPage = useCallback(
    async (page: number) => {
      try {
        dispatch({ type: ActionType.SET_LOADING_GAMES, payload: true })

        const filterParams = getFilterParams()
        const dateRange = getDateRange()

        const query = {
          ...filterParams,
          ...dateRange,
          page,
          per_page: 45,
        } as any

        const { data, meta } = await req('/games/follow', { query })

        dispatch({
          type: ActionType.SET_GAMES,
          payload: {
            games: data || [],
            pagination: meta?.pagination || null,
          },
        })
      } catch (error) {
        console.error('🚨 Error fetching games for page:', error)
        dispatch({
          type: ActionType.SET_GAMES,
          payload: { games: [], pagination: null },
        })
      } finally {
        dispatch({ type: ActionType.SET_LOADING_GAMES, payload: false })
      }
    },
    [getFilterParams, getDateRange]
  )

  /**
   * Fetch more games (pagination)
   */
  const fetchMoreGames = useCallback(async () => {
    if (!state.gamesPagination) {
      return
    }
    if (
      state.gamesPagination.current_page >= state.gamesPagination.total_pages
    ) {
      return
    }

    try {
      const filterParams = getFilterParams()
      const dateRange = getDateRange()

      const query = {
        ...filterParams,
        ...dateRange,
        page: state.gamesPagination.current_page + 1,
      } as any

      const { data, meta } = await req('/games/follow', { query })

      dispatch({
        type: ActionType.APPEND_GAMES,
        payload: {
          games: data || [],
          pagination: meta?.pagination || null,
        },
      })
    } catch (error) {
      console.error('🚨 Error fetching more games:', error)
    }
  }, [state.gamesPagination, getFilterParams, getDateRange])

  /**
   * Fetch organizations tree
   */
  const fetchOrganizationsTree = useCallback(async () => {
    console.log('🔄 MyScheduleContext: Fetching organizations tree')

    try {
      dispatch({ type: ActionType.SET_LOADING_ORGANIZATIONS, payload: true })

      const query: Record<string, any> = {}

      query.membership_type = state.selectedMembershipType

      const { data } = await req('/users/me2/organizations/tree', { query })

      dispatch({
        type: ActionType.SET_ORGANIZATIONS_TREE,
        payload: data || [],
      })
    } catch (error) {
      console.error('🚨 Error fetching organizations tree:', error)
      dispatch({
        type: ActionType.SET_ORGANIZATIONS_TREE,
        payload: [],
      })
    } finally {
      dispatch({ type: ActionType.SET_LOADING_ORGANIZATIONS, payload: false })
    }
  }, [state.selectedMembershipType])

  /**
   * Fetch schedules (filtered by organization)
   */
  const fetchSchedules = useCallback(
    async (search?: string) => {
      try {
        dispatch({ type: ActionType.SET_LOADING_SCHEDULES, payload: true })

        const query: Record<string, any> = {
          page: 1,
          sort: 'ends_at',
          direction: 'desc',
          membership_type: state.selectedMembershipType,
        }

        if (state.selectedOrganization) {
          query.organization_id = state.selectedOrganization.id
          query.recursive = 1
          delete query.membership_type
        }

        if (search) {
          query.query = search
        }

        const { data, meta } = await req('/users/me2/schedules', { query })

        dispatch({
          type: ActionType.SET_SCHEDULES,
          payload: { data: data || [], pagination: meta?.pagination || null },
        })
      } catch (error) {
        console.error('🚨 Error fetching schedules:', error)
        dispatch({
          type: ActionType.SET_SCHEDULES,
          payload: { data: [], pagination: null },
        })
      } finally {
        dispatch({ type: ActionType.SET_LOADING_SCHEDULES, payload: false })
      }
    },
    [state.selectedOrganization, state.selectedMembershipType]
  )

  /**
   * Fetch teams (filtered by organization and schedule)
   */
  const fetchTeams = useCallback(
    async (search?: string) => {
      console.log('🔄 MyScheduleContext: Fetching teams', { search })

      try {
        dispatch({ type: ActionType.SET_LOADING_TEAMS, payload: true })

        const query: Record<string, any> = {
          page: 1,
          sort: 'name',
          direction: 'asc',
          membership_type: state.selectedMembershipType,
        }

        if (state.selectedOrganization) {
          query.organization_id = state.selectedOrganization.id
          query.recursive = 1
          delete query.membership_type
        }

        if (state.selectedSchedule) {
          query.schedule_id = state.selectedSchedule.id
        }

        if (search) {
          query.query = search
        }

        let url = '/users/me2/team-membership'
        // let url = '/users/me2/teams'

        if (state.selectedMembershipType === 'following') {
          url = '/users/me2/teams'
        }

        const { data, meta } = await req(url, { query })

        dispatch({
          type: ActionType.SET_TEAMS,
          payload: { data: data || [], pagination: meta?.pagination || null },
        })
      } catch (error) {
        console.error('🚨 Error fetching teams:', error)
        dispatch({
          type: ActionType.SET_TEAMS,
          payload: { data: [], pagination: null },
        })
      } finally {
        dispatch({ type: ActionType.SET_LOADING_TEAMS, payload: false })
      }
    },
    [
      state.selectedOrganization,
      state.selectedSchedule,
      state.selectedMembershipType,
    ]
  )

  /**
   * Fetch venues (filtered by organization)
   */
  const fetchVenues = useCallback(
    async (search?: string) => {
      try {
        dispatch({ type: ActionType.SET_LOADING_VENUES, payload: true })

        const query: Record<string, any> = {
          page: 1,
          membership_type: state.selectedMembershipType,
        }

        if (state.selectedOrganization) {
          query.organization_id = state.selectedOrganization.id
          query.recursive = 1
          delete query.membership_type
        }

        if (search) {
          query.query = search
        }

        const { data, meta } = await req('/users/me2/venues', { query })

        dispatch({
          type: ActionType.SET_VENUES,
          payload: { data: data || [], pagination: meta?.pagination || null },
        })
      } catch (error) {
        console.error('🚨 Error fetching venues:', error)
        dispatch({
          type: ActionType.SET_VENUES,
          payload: { data: [], pagination: null },
        })
      } finally {
        dispatch({ type: ActionType.SET_LOADING_VENUES, payload: false })
      }
    },
    [state.selectedOrganization, state.selectedMembershipType]
  )

  /**
   * Fetch more schedules (pagination)
   */
  const fetchMoreSchedules = useCallback(async () => {
    if (!state.schedulesPagination) {
      return
    }
    if (
      state.schedulesPagination.current_page >=
      state.schedulesPagination.total_pages
    ) {
      return
    }
    if (state.appendingSchedules) {
      return
    }

    try {
      dispatch({ type: ActionType.SET_APPENDING_SCHEDULES, payload: true })

      const query: Record<string, any> = {
        page: state.schedulesPagination.current_page + 1,
        sort: 'ends_at',
        direction: 'desc',
        membership_type: state.selectedMembershipType,
      }

      if (state.selectedOrganization) {
        query.organization_id = state.selectedOrganization.id
        query.recursive = 1
      }

      if (state.schedulesSearchQuery) {
        query.query = state.schedulesSearchQuery
      }

      const { data, meta } = await req('/users/me2/schedules', { query })

      dispatch({
        type: ActionType.APPEND_SCHEDULES,
        payload: { data: data || [], pagination: meta?.pagination || null },
      })
    } catch (error) {
      console.error('🚨 Error fetching more schedules:', error)
    } finally {
      dispatch({ type: ActionType.SET_APPENDING_SCHEDULES, payload: false })
    }
  }, [
    state.schedulesPagination,
    state.appendingSchedules,
    state.selectedMembershipType,
    state.selectedOrganization,
    state.schedulesSearchQuery,
  ])

  /**
   * Fetch more teams (pagination)
   */
  const fetchMoreTeams = useCallback(async () => {
    if (!state.teamsPagination) {
      return
    }
    if (
      state.teamsPagination.current_page >= state.teamsPagination.total_pages
    ) {
      return
    }
    if (state.appendingTeams) {
      return
    }

    try {
      dispatch({ type: ActionType.SET_APPENDING_TEAMS, payload: true })

      const query: Record<string, any> = {
        page: state.teamsPagination.current_page + 1,
        sort: 'name',
        direction: 'asc',
        membership_type: state.selectedMembershipType,
      }

      if (state.selectedOrganization) {
        query.organization_id = state.selectedOrganization.id
        query.recursive = 1
      }

      if (state.selectedSchedule) {
        query.schedule_id = state.selectedSchedule.id
      }

      if (state.teamsSearchQuery) {
        query.query = state.teamsSearchQuery
      }

      let url = '/users/me2/team-membership'
      // let url = '/users/me2/teams'

      if (state.selectedMembershipType === 'following') {
        url = '/users/me2/teams'
      }

      const { data, meta } = await req(url, { query })

      dispatch({
        type: ActionType.APPEND_TEAMS,
        payload: { data: data || [], pagination: meta?.pagination || null },
      })
    } catch (error) {
      console.error('🚨 Error fetching more teams:', error)
    } finally {
      dispatch({ type: ActionType.SET_APPENDING_TEAMS, payload: false })
    }
  }, [
    state.teamsPagination,
    state.appendingTeams,
    state.selectedMembershipType,
    state.selectedOrganization,
    state.selectedSchedule,
    state.teamsSearchQuery,
  ])

  /**
   * Search schedules (reset and fetch with search query)
   */
  const searchSchedules = useCallback(
    (query: string) => {
      dispatch({ type: ActionType.SET_SCHEDULES_SEARCH_QUERY, payload: query })
      fetchSchedules(query)
    },
    [fetchSchedules]
  )

  /**
   * Search teams (reset and fetch with search query)
   */
  const searchTeams = useCallback(
    (query: string) => {
      dispatch({ type: ActionType.SET_TEAMS_SEARCH_QUERY, payload: query })
      fetchTeams(query)
    },
    [fetchTeams]
  )

  /**
   * Fetch more venues (pagination)
   */
  const fetchMoreVenues = useCallback(async () => {
    if (!state.venuesPagination) {
      return
    }
    if (
      state.venuesPagination.current_page >= state.venuesPagination.total_pages
    ) {
      return
    }
    if (state.appendingVenues) {
      return
    }

    try {
      dispatch({ type: ActionType.SET_APPENDING_VENUES, payload: true })

      const query: Record<string, any> = {
        page: state.venuesPagination.current_page + 1,
        membership_type: state.selectedMembershipType,
      }

      if (state.selectedOrganization) {
        query.organization_id = state.selectedOrganization.id
        query.recursive = 1
        delete query.membership_type
      }

      if (state.venuesSearchQuery) {
        query.query = state.venuesSearchQuery
      }

      const { data, meta } = await req('/users/me2/venues', { query })

      dispatch({
        type: ActionType.APPEND_VENUES,
        payload: { data: data || [], pagination: meta?.pagination || null },
      })
    } catch (error) {
      console.error('🚨 Error fetching more venues:', error)
    } finally {
      dispatch({ type: ActionType.SET_APPENDING_VENUES, payload: false })
    }
  }, [
    state.venuesPagination,
    state.appendingVenues,
    state.selectedMembershipType,
    state.selectedOrganization,
    state.venuesSearchQuery,
  ])

  /**
   * Search venues (reset and fetch with search query)
   */
  const searchVenues = useCallback(
    (query: string) => {
      dispatch({ type: ActionType.SET_VENUES_SEARCH_QUERY, payload: query })
      fetchVenues(query)
    },
    [fetchVenues]
  )

  /**
   * Fetch game dates to determine smart default time period
   */
  const fetchDates = useCallback(async () => {
    console.log('🔄 MyScheduleContext: Fetching dates')

    try {
      dispatch({ type: ActionType.SET_LOADING_DATES, payload: true })

      const today = dayjs()
      const startsAfter = today.subtract(6, 'month').format()
      const startsBefore = today.add(6, 'month').format()

      const { data } = await req('/games/dates', {
        query: {
          starts_after: startsAfter,
          starts_before: startsBefore,
          membership_type: state.selectedMembershipType,
        },
      })

      const normalizedDates =
        data?.map((d: string | string[]) => {
          const raw = Array.isArray(d) ? d[0] : d
          return dayjs(raw).format('YYYY-MM-DD')
        }) || []

      const todayFormatted = today.format('YYYY-MM-DD')
      const hasTodayGames = normalizedDates.includes(todayFormatted)
      const hasUpcomingGames = normalizedDates.some((dateStr: string) =>
        dayjs(dateStr).isAfter(today, 'day')
      )

      const defaultTimePeriod: TimePeriod = hasTodayGames
        ? 'TODAY'
        : hasUpcomingGames
        ? 'UPCOMING'
        : 'PAST'

      dispatch({
        type: ActionType.SET_DATES,
        payload: {
          dates: data || [],
          hasTodayGames,
          hasUpcomingGames,
          defaultTimePeriod,
        },
      })
    } catch (error) {
      console.error('🚨 Error fetching dates:', error)
      dispatch({
        type: ActionType.SET_DATES,
        payload: {
          dates: [],
          hasTodayGames: false,
          hasUpcomingGames: false,
        },
      })
    } finally {
      dispatch({ type: ActionType.SET_LOADING_DATES, payload: false })
    }
  }, [state.selectedMembershipType])

  /**
   * Auto-fetch games when filters change
   */
  useEffect(() => {
    // Only fetch games after dates are loaded (smart default is set) AND the
    // opening period is final. `MyScheduleList` may upgrade the games-only
    // period after consulting events; fetching before that settles fired two
    // requests — one per period — with no sequencing guard, so the slower one
    // won and the list showed a period the control no longer said.
    if (!state.filtersLoaded || !state.datesLoaded || !state.periodResolved) {
      return
    }

    fetchGames()
  }, [
    state.filtersLoaded,
    state.datesLoaded,
    state.periodResolved,
    state.selectedOrganization?.id,
    state.selectedSchedule?.id,
    state.selectedTeam?.id,
    state.selectedVenue?.id,
    state.selectedFacility?.id,
    state.selectedDateRange,
    state.selectedTimePeriod,
    state.selectedSortOrder,
    fetchGames,
  ])

  /**
   * Fetch filter options on mount
   */
  useEffect(() => {
    if (!state.filtersLoaded) {
      return
    }

    fetchOrganizationsTree()
    fetchSchedules()
    fetchTeams()
    fetchVenues()
  }, [state.filtersLoaded])

  /**
   * Fetch dates once to determine smart default time period
   */
  useEffect(() => {
    if (!state.filtersLoaded) {
      return
    }
    if (state.datesLoaded) {
      return
    }

    fetchDates()
  }, [state.filtersLoaded, state.datesLoaded, fetchDates])

  /**
   * Re-fetch schedules when organization changes
   */
  useEffect(() => {
    if (!state.filtersLoaded) {
      return
    }
    fetchSchedules()
  }, [state.selectedOrganization?.id])

  /**
   * Re-fetch teams when organization or schedule changes
   */
  useEffect(() => {
    if (!state.filtersLoaded) {
      return
    }
    fetchTeams()
  }, [state.selectedOrganization?.id, state.selectedSchedule?.id])

  /**
   * Re-fetch venues when organization changes
   */
  useEffect(() => {
    if (!state.filtersLoaded) {
      return
    }
    fetchVenues()
  }, [state.selectedOrganization?.id])

  /**
   * Re-fetch all filter options when membership type changes
   */
  useEffect(() => {
    if (!state.filtersLoaded) {
      return
    }
    fetchOrganizationsTree()
    fetchSchedules()
    fetchTeams()
    fetchVenues()
  }, [state.selectedMembershipType])

  // Setter functions
  const setSelectedOrganization = useCallback((org: Organization | null) => {
    dispatch({ type: ActionType.SET_SELECTED_ORGANIZATION, payload: org })
  }, [])

  const setSelectedSchedule = useCallback((schedule: Schedule | null) => {
    dispatch({ type: ActionType.SET_SELECTED_SCHEDULE, payload: schedule })
  }, [])

  const setSelectedTeam = useCallback((team: Team | null) => {
    dispatch({ type: ActionType.SET_SELECTED_TEAM, payload: team })
  }, [])

  const setSelectedVenue = useCallback((venue: Venue | null) => {
    dispatch({ type: ActionType.SET_SELECTED_VENUE, payload: venue })
  }, [])

  const setSelectedFacility = useCallback((facility: Facility | null) => {
    dispatch({ type: ActionType.SET_SELECTED_FACILITY, payload: facility })
  }, [])

  const setSelectedDateRange = useCallback((range: [Dayjs, Dayjs] | null) => {
    dispatch({ type: ActionType.SET_SELECTED_DATE_RANGE, payload: range })
  }, [])

  const setSelectedTimePeriod = useCallback((period: TimePeriod) => {
    dispatch({ type: ActionType.SET_SELECTED_TIME_PERIOD, payload: period })
  }, [])

  const setPeriodResolved = useCallback(() => {
    dispatch({ type: ActionType.SET_PERIOD_RESOLVED })
  }, [])

  const setSelectedMembershipType = useCallback((type: MembershipType) => {
    dispatch({ type: ActionType.SET_SELECTED_MEMBERSHIP_TYPE, payload: type })
  }, [])

  const setSelectedSortOrder = useCallback((order: SortOrder) => {
    dispatch({ type: ActionType.SET_SELECTED_SORT_ORDER, payload: order })
  }, [])

  const setCalendarMode = useCallback((mode: boolean) => {
    dispatch({ type: ActionType.SET_CALENDAR_MODE, payload: mode })
  }, [])

  const clearFilter = useCallback(
    (
      filterType:
        | 'organization'
        | 'schedule'
        | 'team'
        | 'venue'
        | 'facility'
        | 'dateRange'
        | 'timePeriod'
    ) => {
      dispatch({ type: ActionType.CLEAR_FILTER, payload: filterType })
    },
    []
  )

  const clearAllFilters = useCallback(() => {
    dispatch({ type: ActionType.CLEAR_ALL_FILTERS })
  }, [])

  const value: MyScheduleContextValue = {
    ...state,
    scheduleQuery,
    setSelectedOrganization,
    setSelectedSchedule,
    setSelectedTeam,
    setSelectedVenue,
    setSelectedFacility,
    setSelectedDateRange,
    setSelectedTimePeriod,
    setPeriodResolved,
    entityFilters,
    setSelectedMembershipType,
    setSelectedSortOrder,
    setCalendarMode,
    fetchGames,
    fetchGamesPage,
    fetchMoreGames,
    fetchOrganizationsTree,
    fetchSchedules,
    fetchTeams,
    fetchVenues,
    fetchMoreSchedules,
    fetchMoreTeams,
    fetchMoreVenues,
    searchSchedules,
    searchTeams,
    searchVenues,
    clearFilter,
    clearAllFilters,
  }

  return (
    <MyScheduleContext.Provider value={value}>
      {children}
    </MyScheduleContext.Provider>
  )
}

export const useMyScheduleContext = () => {
  const context = useContext(MyScheduleContext)
  if (context === undefined) {
    throw new Error(
      'useMyScheduleContext must be used within MyScheduleContextProvider'
    )
  }
  return context
}
